import type { CountryKey, DateParts } from "@/lib/dateIntent";
import {
  citiesEnglishFor,
  citySlug,
  countryLabelEN,
  dateWindowSentences,
} from "@/lib/keywordMesh";

type Props = {
  country: CountryKey;
  date: DateParts;
  price24Label?: string;
  price22Label?: string;
  price21Label?: string;
  unitLabel?: string;
  /**
   * Optional override: when supplied, the three price tiles render these
   * labels/values instead of the hardcoded 24K/22K/21K tiles. Used by the
   * silver hubs which don't have karats.
   */
  tiles?: { label: string; value?: string }[];
  /** Override the H2 metal noun. Defaults to "Gold". */
  metalLabel?: string;
};

/**
 * Date-intent block: renders directly under the hero on each gold hub.
 * Provides a semantic <time>, three live-price tiles (text labels passed
 * in to keep this presentational), one 80-word natural paragraph, and
 * a horizontal scrollable strip of city pills whose anchors are
 * `${citySlug}-${date.slug}` so SERP sitelinks can resolve them.
 */
export function TodayDateBlock({
  country,
  date,
  price24Label,
  price22Label,
  price21Label,
  unitLabel,
  tiles,
  metalLabel = "Gold",
}: Props) {
  const label = countryLabelEN(country);
  const cities = citiesEnglishFor(country);
  const top3 = cities.slice(0, 3).join(", ");
  const dateWindow = dateWindowSentences(country, date);
  const unit =
    unitLabel ??
    (country === "pakistan"
      ? "per tola"
      : country === "india"
        ? "per 10 gram"
        : "per gram");

  return (
    <section
      className="mx-auto max-w-6xl px-4 py-6 scroll-mt-24"
      aria-label={`Today's ${metalLabel.toLowerCase()} rate in ${label}`}
    >
      <div className="rounded-2xl border border-border bg-card p-5 md:p-6 shadow-sm" style={{ minHeight: 280 }}>
        <h2
          id={`today-gold-rate-${date.slug}`}
          data-speakable="today-rate"
          className="text-lg md:text-xl font-bold"
        >
          Today's {metalLabel} Rate in {label} —{" "}
          <span data-nosnippet><time dateTime={date.isoWithTZ}>{date.longEN}</time></span>
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          <span data-nosnippet>Last updated: {date.nowLabel}</span>
        </p>

        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          {tiles
            ? tiles.map((t, i) => (
                <PriceTile key={i} karat={t.label} value={t.value} unit="" highlight={i === 0} />
              ))
            : (
              <>
                <PriceTile karat="24K" value={price24Label} unit={unit} highlight />
                <PriceTile karat="22K" value={price22Label} unit={unit} />
                <PriceTile karat="21K" value={price21Label} unit={unit} />
              </>
            )}
        </div>

        <p className="mt-4 text-sm text-foreground/85 leading-relaxed max-w-3xl">
          The live {label} gold rate on <span data-nosnippet>{date.longEN}</span> is updated every 10 minutes from the
          international XAU/USD spot price and the day's exchange rate. Top cities including {top3}
          are tracked below, with per gram, {unit} and per 10 gram quotes for 24K, 22K, 21K and 18K
          jewellery and bullion. Tap any city pill to jump to its dated section.
        </p>

        <ul className="mt-3 space-y-1 text-xs text-muted-foreground" data-nosnippet>
          {dateWindow.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ul>

        <div className="mt-4 -mx-1 flex flex-wrap gap-2 pb-1">
          {cities.map((c) => (
            <a
              key={c}
              href={`#${citySlug(c)}-${date.slug}`}
              className="rounded-full border border-border bg-muted/40 px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted whitespace-nowrap"
            >
              <span data-nosnippet>{c} {date.longEN}</span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function PriceTile({
  karat,
  value,
  unit,
  highlight,
}: {
  karat: string;
  value?: string;
  unit: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`rounded-xl border p-3 ${
        highlight
          ? "border-gold/50 bg-gold/5"
          : "border-border bg-background"
      }`}
    >
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">
        {unit ? `${karat} · ${unit}` : karat}
      </div>
      <div className="mt-1 text-lg md:text-xl font-extrabold tabular-nums">
        {value ?? "—"}
      </div>
    </div>
  );
}